import { faqPageSchema, type Faq } from "@/data/serviceFaqs";
import { serviceAreas } from "@/data/serviceAreas";

const SITE = "https://apexav.com";

/** Absolute-izes an asset path so crawlers always get a fully qualified URL. */
export function abs(url: string): string {
  return url.startsWith("http") ? url : `${SITE}${url.startsWith("/") ? "" : "/"}${url}`;
}

export type ServiceGraphOptions = {
  /** Path segment under /services/ — e.g. "lighting". */
  slug: string;
  /** Service name, e.g. "Smart Lighting Control". */
  name: string;
  alternateName?: string[];
  /** One-to-two sentence service description. */
  description: string;
  /** <title> of the page, reused as the WebPage name. */
  pageTitle: string;
  /** Meta description, reused as the WebPage description. */
  pageDescription?: string;
  /** Hero image — relative asset path or absolute URL. */
  image: string;
  faqs?: Faq[];
  /** schema.org serviceType; defaults to `name`. */
  serviceType?: string;
  brand?: string;
  /** Lowest realistic installed price in USD. Omit to skip the Offer price spec. */
  minPrice?: number;
  /** Human-readable pricing explanation shown in the PriceSpecification. */
  priceDescription?: string;
  /** Individual deliverables, rendered as an OfferCatalog. */
  offerCatalog?: string[];
  /** Breadcrumb leaf label; defaults to `name`. */
  breadcrumbName?: string;
  /** Extra graph nodes for this page (VideoObject, Product, etc.). */
  extraNodes?: Record<string, unknown>[];
};

/**
 * Builds the standard rich-results graph shared by every /services/* page:
 * FAQPage + Service (with provider, areaServed, offers, offer catalog)
 * + WebPage + BreadcrumbList.
 */
export function serviceGraph(o: ServiceGraphOptions) {
  const url = `${SITE}/services/${o.slug}`;
  const image = abs(o.image);
  const faqs = o.faqs ?? [];

  const graph: Record<string, unknown>[] = [];

  if (faqs.length) {
    graph.push({
      ...faqPageSchema(faqs, url),
      "@id": `${url}#faq`,
      inLanguage: "en-US",
      about: { "@id": `${url}#service` },
    });
  }

  graph.push({
    "@type": "Service",
    "@id": `${url}#service`,
    name: o.name,
    ...(o.alternateName?.length ? { alternateName: o.alternateName } : {}),
    description: o.description,
    url,
    image,
    provider: { "@id": `${SITE}/#organization` },
    serviceType: o.serviceType ?? o.name,
    category: "Home Improvement",
    ...(o.brand ? { brand: { "@type": "Brand", name: o.brand } } : {}),
    areaServed: [
      { "@type": "AdministrativeArea", name: "Denver Metro & Front Range, Colorado" },
      ...serviceAreas.map((a) => ({
        "@type": "City",
        name: a.name,
        address: {
          "@type": "PostalAddress",
          addressLocality: a.name,
          addressRegion: "CO",
          addressCountry: "US",
        },
      })),
    ],
    audience: {
      "@type": "Audience",
      audienceType: "Homeowners and businesses in the Denver metro area",
    },
    offers: {
      "@type": "Offer",
      url,
      availability: "https://schema.org/InStock",
      ...(o.minPrice
        ? {
            priceSpecification: {
              "@type": "PriceSpecification",
              price: o.minPrice,
              minPrice: o.minPrice,
              priceCurrency: "USD",
              valueAddedTaxIncluded: false,
              ...(o.priceDescription ? { description: o.priceDescription } : {}),
            },
          }
        : {}),
    },
    ...(o.offerCatalog?.length
      ? {
          hasOfferCatalog: {
            "@type": "OfferCatalog",
            name: `${o.name} Services`,
            itemListElement: o.offerCatalog.map((item) => ({
              "@type": "Offer",
              itemOffered: { "@type": "Service", name: item },
            })),
          },
        }
      : {}),
    mainEntityOfPage: { "@id": `${url}#webpage` },
  });

  graph.push({
    "@type": "WebPage",
    "@id": `${url}#webpage`,
    url,
    name: o.pageTitle,
    ...(o.pageDescription ? { description: o.pageDescription } : {}),
    inLanguage: "en-US",
    isPartOf: { "@id": `${SITE}/#website` },
    primaryImageOfPage: { "@type": "ImageObject", url: image },
    breadcrumb: { "@id": `${url}#breadcrumb` },
  });

  graph.push({
    "@type": "BreadcrumbList",
    "@id": `${url}#breadcrumb`,
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
      { "@type": "ListItem", position: 2, name: "Services", item: `${SITE}/services` },
      { "@type": "ListItem", position: 3, name: o.breadcrumbName ?? o.name, item: url },
    ],
  });

  if (o.extraNodes?.length) graph.push(...o.extraNodes);

  return { "@context": "https://schema.org", "@graph": graph };
}
